import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
    LayoutDashboard, Users, CheckSquare, UserCheck, Package,
    Wifi, Phone, Clock, FileText, Ticket, Bell,
    LogOut, ChevronLeft, ChevronRight, Menu, X, Zap,
    Lock, QrCode, Building2, Mail, Phone as PhoneIcon, Calendar, FolderOpen,
    ShoppingCart, Receipt
} from 'lucide-react'
import { useStaffAuthStore } from '../../../store/staffAuthStore'
import { supabase } from '../../../lib/supabase'
import toast from 'react-hot-toast'
import { motion, AnimatePresence, FadeUp, SlideInRight } from './StaffAnimations'

const NAV = [
    { to: '/staff/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/staff/tasks', icon: CheckSquare, label: 'Tasks' },
    { to: '/staff/customers', icon: UserCheck, label: 'Customers' },
    { to: '/staff/inventory', icon: Package, label: 'Parts' },
    { to: '/staff/bsnl', icon: Wifi, label: 'BSNL' },
    { to: '/staff/call-logs', icon: Phone, label: 'Call Logs' },
    { to: '/staff/attendance', icon: Clock, label: 'Attendance' },
    { to: '/staff/quotations', icon: FileText, label: 'Quotations' },
    { to: '/staff/billing', icon: Receipt, label: 'Billing' },
    { to: '/staff/tickets', icon: Ticket, label: 'Tickets' },
    { to: '/staff/bookings', icon: ShoppingCart, label: 'Bookings' },
    { to: '/staff/documents', icon: FolderOpen, label: 'Documents' },
    { to: '/staff/team', icon: Users, label: 'Team' },
    { to: '/staff/notify', icon: Bell, label: 'Notifications' },
]

function StaffAvatar({ profile, size = 7, textSize = 'text-xs' }) {
    const colors = ['bg-blue-600', 'bg-purple-600', 'bg-green-600', 'bg-amber-600', 'bg-rose-600', 'bg-indigo-600']
    const colorIdx = profile?.name ? profile.name.charCodeAt(0) % colors.length : 0
    const px = size * 4
    if (profile?.profile_photo_url) {
        return (
            <img
                src={profile.profile_photo_url}
                alt={profile.name}
                className="rounded-full object-cover shrink-0"
                style={{ width: px, height: px }}
            />
        )
    }
    return (
        <div className={`rounded-full ${colors[colorIdx]} flex items-center justify-center ${textSize} font-bold text-white shrink-0`} style={{ width: px, height: px }}>
            {profile?.name?.[0]?.toUpperCase() || '?'}
        </div>
    )
}

function ProfileSlideover({ onClose }) {
    const { profile, logout } = useStaffAuthStore()
    const navigate = useNavigate()
    const [showChangePassword, setShowChangePassword] = useState(false)
    const [showQr, setShowQr] = useState(false)
    const [pw, setPw] = useState({ current: '', newPw: '', confirm: '' })
    const [pwLoading, setPwLoading] = useState(false)

    const handleLogout = async () => {
        await logout()
        toast.success('Logged out')
        navigate('/staff')
    }

    const handleChangePassword = async (e) => {
        e.preventDefault()
        if (pw.newPw !== pw.confirm) { toast.error('Passwords do not match'); return }
        if (pw.newPw.length < 6) { toast.error('Password must be at least 6 characters'); return }
        setPwLoading(true)
        const { error } = await supabase.auth.updateUser({ password: pw.newPw })
        setPwLoading(false)
        if (error) { toast.error(error.message); return }
        toast.success('Password updated successfully')
        setShowChangePassword(false)
        setPw({ current: '', newPw: '', confirm: '' })
    }

    const roleBadge = {
        ADMIN: 'bg-purple-500/20 text-purple-300 border border-purple-500/30',
        MANAGER: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
        STAFF: 'bg-slate-500/20 text-slate-300 border border-slate-500/30',
    }

    return (
        <AnimatePresence>
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 bg-black/50 z-40"
                onClick={onClose}
            />

            {/* Slide-over panel */}
            <SlideInRight className="fixed right-0 top-0 bottom-0 w-80 bg-slate-900 border-l border-white/8 z-50 flex flex-col shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
                    <h2 className="font-semibold text-white text-sm">My Profile</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition">
                        <X size={18} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {/* Profile header */}
                    <div className="p-5 flex flex-col items-center gap-3 border-b border-white/5">
                        {profile?.profile_photo_url ? (
                            <img
                                src={profile.profile_photo_url}
                                alt={profile.name}
                                className="w-20 h-20 rounded-full object-cover ring-4 ring-blue-500/30"
                            />
                        ) : (
                            <div className="w-20 h-20 rounded-full bg-blue-600 flex items-center justify-center text-2xl font-bold text-white ring-4 ring-blue-500/30">
                                {profile?.name?.[0]?.toUpperCase() || '?'}
                            </div>
                        )}
                        <div className="text-center">
                            <p className="text-white font-semibold text-lg">{profile?.name}</p>
                            {profile?.username && (
                                <p className="text-slate-400 text-sm">@{profile.username}</p>
                            )}
                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium mt-1 inline-block ${roleBadge[profile?.role] || roleBadge.STAFF}`}>
                                {profile?.role}
                            </span>
                        </div>
                    </div>

                    {/* Info rows */}
                    <div className="p-4 space-y-2">
                        {profile?.phone_number && (
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50">
                                <PhoneIcon size={15} className="text-slate-400 shrink-0" />
                                <div>
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Phone</p>
                                    <p className="text-sm text-white">{profile.phone_number}</p>
                                </div>
                            </div>
                        )}
                        {profile?.email && (
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50">
                                <Mail size={15} className="text-slate-400 shrink-0" />
                                <div>
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Email</p>
                                    <p className="text-sm text-white truncate">{profile.email}</p>
                                </div>
                            </div>
                        )}
                        {profile?.department && (
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50">
                                <Building2 size={15} className="text-slate-400 shrink-0" />
                                <div>
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Department</p>
                                    <p className="text-sm text-white">{profile.department}</p>
                                </div>
                            </div>
                        )}
                        {profile?.created_at && (
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50">
                                <Calendar size={15} className="text-slate-400 shrink-0" />
                                <div>
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Joined</p>
                                    <p className="text-sm text-white">{new Date(profile.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Change Password */}
                    <div className="px-4 pb-2">
                        <button
                            onClick={() => setShowChangePassword(!showChangePassword)}
                            className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 hover:bg-slate-700/50 transition text-left"
                        >
                            <Lock size={15} className="text-slate-400 shrink-0" />
                            <span className="text-sm text-slate-300">Change Password</span>
                        </button>
                        {showChangePassword && (
                            <form onSubmit={handleChangePassword} className="mt-2 p-3 bg-slate-800/50 rounded-xl space-y-2">
                                <div>
                                    <label className="text-[10px] text-slate-400 uppercase tracking-wide block mb-1">New Password</label>
                                    <input
                                        type="password"
                                        value={pw.newPw}
                                        onChange={e => setPw(p => ({ ...p, newPw: e.target.value }))}
                                        className="w-full bg-slate-700 border border-white/8 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                                        placeholder="Min 6 characters"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] text-slate-400 uppercase tracking-wide block mb-1">Confirm Password</label>
                                    <input
                                        type="password"
                                        value={pw.confirm}
                                        onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))}
                                        className="w-full bg-slate-700 border border-white/8 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                                        placeholder="Repeat password"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={pwLoading}
                                    className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-medium transition"
                                >
                                    {pwLoading ? 'Updating...' : 'Update Password'}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Company QR */}
                    <div className="px-4 pb-4">
                        <button
                            onClick={() => setShowQr(!showQr)}
                            className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 hover:bg-slate-700/50 transition text-left"
                        >
                            <QrCode size={15} className="text-slate-400 shrink-0" />
                            <span className="text-sm text-slate-300">Scan to Pay — Company QR</span>
                        </button>
                        {showQr && (
                            <div className="mt-2 p-3 bg-slate-800/50 rounded-xl flex flex-col items-center gap-2">
                                <p className="text-xs text-slate-400">Abha Enterprises Payment QR</p>
                                <img
                                    src="/company_qr.jpeg"
                                    alt="Company QR"
                                    className="w-48 h-48 object-contain bg-white rounded-xl p-2"
                                    onError={e => { e.target.style.display = 'none' }}
                                />
                                <p className="text-[10px] text-slate-500">Show this QR to receive payments</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Logout */}
                <div className="p-4 border-t border-white/5">
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 p-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 transition text-sm font-medium"
                    >
                        <LogOut size={16} />
                        Sign Out
                    </button>
                </div>
            </SlideInRight>
        </AnimatePresence>
    )
}

export { StaffAvatar }

export default function StaffLayout({ children }) {
    const [collapsed, setCollapsed] = useState(false)
    const [mobileOpen, setMobileOpen] = useState(false)
    const [profileOpen, setProfileOpen] = useState(false)
    const location = useLocation()
    const navigate = useNavigate()
    const { profile } = useStaffAuthStore()

    const SidebarContent = () => (
        <div className="flex flex-col h-full">
            {/* Logo */}
            <div className={`flex items-center gap-3 px-4 py-5 border-b border-white/5 ${collapsed ? 'justify-center' : ''}`}>
                <img
                    src="/new_logo.png"
                    alt="AbhaFlow"
                    className="w-8 h-8 rounded-lg object-contain shrink-0"
                    onError={e => { e.target.style.display = 'none' }}
                />
                {!collapsed && (
                    <div>
                        <p className="text-sm font-bold text-white">AbhaFlow</p>
                        <p className="text-[10px] text-slate-400">Staff Portal</p>
                    </div>
                )}
            </div>

            {/* Nav */}
            <nav className="flex-1 overflow-y-auto no-scrollbar py-3 px-2 space-y-0.5">
                {NAV.map(({ to, icon: Icon, label }) => {
                    const active = location.pathname === to || location.pathname.startsWith(to + '/')
                    return (
                        <Link
                            key={to}
                            to={to}
                            onClick={() => setMobileOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all
                                ${active ? 'bg-blue-600/20 text-blue-400 font-medium' : 'text-slate-400 hover:bg-white/5 hover:text-white'}
                                ${collapsed ? 'justify-center' : ''}
                            `}
                            title={collapsed ? label : ''}
                        >
                            <Icon size={17} className="shrink-0" />
                            {!collapsed && <span>{label}</span>}
                        </Link>
                    )
                })}
            </nav>

            {/* Profile + logout */}
            <div className="p-3 border-t border-white/5 space-y-1">
                {!collapsed && profile && (
                    <button
                        onClick={() => setProfileOpen(true)}
                        className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-white/5 transition text-left"
                    >
                        <StaffAvatar profile={profile} size={7} textSize="text-xs" />
                        <div className="min-w-0">
                            <p className="text-xs font-medium text-white truncate">{profile.name}</p>
                            <p className="text-[10px] text-slate-400 truncate">{profile.role}</p>
                        </div>
                    </button>
                )}
            </div>
        </div>
    )

    return (
        <div className="flex h-screen bg-slate-950 overflow-hidden">
            {/* Desktop sidebar */}
            <aside className={`hidden md:flex flex-col bg-slate-900 border-r border-white/5 transition-all duration-200 shrink-0 ${collapsed ? 'w-16' : 'w-56'}`}>
                <SidebarContent />
            </aside>

            {/* Collapse toggle button */}
            <button
                onClick={() => setCollapsed(!collapsed)}
                className="hidden md:flex absolute top-1/2 -translate-y-1/2 w-5 h-10 bg-slate-800 border border-white/10 rounded-r-lg items-center justify-center text-slate-400 hover:text-white z-20 transition-all duration-200"
                style={{ left: collapsed ? '3.75rem' : '13.75rem' }}
            >
                {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
            </button>

            {/* Mobile overlay */}
            {mobileOpen && (
                <div className="fixed inset-0 z-40 md:hidden">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
                    <aside className="absolute left-0 top-0 bottom-0 w-56 bg-slate-900 border-r border-white/5 z-50">
                        <SidebarContent />
                    </aside>
                </div>
            )}

            {/* Main */}
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
                {/* Topbar */}
                <header className="h-14 bg-slate-900 border-b border-white/5 flex items-center px-4 gap-3 shrink-0">
                    <button className="md:hidden text-slate-400 hover:text-white" onClick={() => setMobileOpen(true)}>
                        <Menu size={20} />
                    </button>
                    <div className="flex-1" />
                    <span className="text-xs text-slate-500 hidden sm:block">
                        {new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </span>
                    {/* Profile avatar button */}
                    {profile && (
                        <button
                            onClick={() => setProfileOpen(true)}
                            className="flex items-center gap-2 hover:bg-white/5 rounded-xl px-2 py-1 transition"
                        >
                            <StaffAvatar profile={profile} size={7} textSize="text-xs" />
                            <span className="hidden sm:block text-xs text-slate-300 font-medium">{profile.name?.split(' ')[0]}</span>
                        </button>
                    )}
                </header>

                {/* Content */}
                <main className="flex-1 overflow-y-auto p-4 md:p-6">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={location.pathname}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                        >
                            {children}
                        </motion.div>
                    </AnimatePresence>
                </main>
            </div>

            {/* Profile slide-over */}
            {profileOpen && <ProfileSlideover onClose={() => setProfileOpen(false)} />}
        </div>
    )
}
